import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getVehicleServiceStatusesAPI } from "services/api/fleet/vehicles/services/api";
import { initialFleetVehicleServiceSearchForm } from "services/api/fleet/vehicles/services/consts";
import { VehicleServiceStatus } from "services/api/fleet/vehicles/services/types";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";
import { TextField } from "ui/components/TextField/TextField";
import {
  DEFAULT_SERVICE_STATUS_HEX,
  SERVICE_STATUS_HEX,
} from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleServiceSearchPage/consts";

import { SERVICE_OPEN_OPTIONS } from "../../consts";
import { ServiceSearchFiltersProps } from "./types";

/** Filtri zajedničke pretrage naloga — uz status i servisera i kaskada vozila. */
const ServiceSearchFilters: React.FC<ServiceSearchFiltersProps> = (props) => {
  const { formValues, onSearchChange } = props;

  const { t } = useTranslate();

  const [statuses, setStatuses] = useState<VehicleServiceStatus[]>([]);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setStatuses(await getVehicleServiceStatusesAPI());
    } catch {
      setStatuses([]);
    }
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onSearchChange({ ...formValues, search: e.target.value });
    },
    [formValues, onSearchChange]
  );

  const handleStatusChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleServiceStatusId: item.value || null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleContactChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({ ...formValues, contactId: item.value || null });
    },
    [formValues, onSearchChange]
  );

  // Promjena marke prazni model i tip, promjena modela prazni tip — inače ostane
  // nemoguća kombinacija i popis se isprazni.
  const handleManufacturerChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleManufacturerId: item.value || null,
        vehicleModelId: null,
        vehicleTypeId: null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleModelChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleModelId: item.value || null,
        vehicleTypeId: null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleTypeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({ ...formValues, vehicleTypeId: item.value || null });
    },
    [formValues, onSearchChange]
  );

  // Dropdown radi sa stringovima, a filter je trostanjski (svi / otvoreni / zatvoreni).
  const handleOpenChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        isOpen: item.value ? item.value === "true" : null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onSearchChange({ ...formValues, [name]: value });
    },
    [formValues, onSearchChange]
  );

  const handleResetFormClick = useCallback(() => {
    onSearchChange(initialFleetVehicleServiceSearchForm);
  }, [onSearchChange]);

  const statusItems: DropdownOption[] = statuses.map((status) => ({
    value: status.id,
    text: status.name,
    color: SERVICE_STATUS_HEX[status.color ?? ""] ?? DEFAULT_SERVICE_STATUS_HEX,
  }));

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[240px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder="Naziv naloga ili servis..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="min-w-[180px]">
        <Dropdown
          name="vehicleServiceStatusId"
          placeholder="Status"
          options={statusItems}
          value={formValues.vehicleServiceStatusId}
          onChange={handleStatusChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[180px]">
        <Dropdown
          name="isOpen"
          placeholder="Svi nalozi"
          options={SERVICE_OPEN_OPTIONS}
          value={formValues.isOpen === null ? null : String(formValues.isOpen)}
          onChange={handleOpenChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <ContactDropdown
          name="contactId"
          placeholder="Serviser"
          value={formValues.contactId}
          onChange={handleContactChange}
          contactGroupIds={["A"]}
          width="full"
          allowCreate={false}
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleManufacturerDropdown
          name="vehicleManufacturerId"
          placeholder={t("admin.vehicle.manufacturer.placeholder")}
          value={formValues.vehicleManufacturerId}
          onChange={handleManufacturerChange}
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleModelDropdown
          name="vehicleModelId"
          placeholder={t("admin.vehicle.model.placeholder")}
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          value={formValues.vehicleModelId}
          onChange={handleModelChange}
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleTypeDropdown
          name="vehicleTypeId"
          placeholder={t("admin.vehicle.type.placeholder")}
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          vehicleModelId={formValues.vehicleModelId}
          value={formValues.vehicleTypeId}
          onChange={handleTypeChange}
          width="full"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="receivedDateFrom"
          label="Zaprimljeno od - do"
          value={formValues.receivedDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="receivedDateTo"
          value={formValues.receivedDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(ServiceSearchFilters);
